import React, { useState, useRef, useEffect } from "react";
import {
  MdArrowDropUp,
  MdRefresh,
} from "react-icons/md";
import Card from "components/card";
import {
  lineChartDataTotalSpent,
  lineChartOptionsTotalSpent,
} from "variables/charts";
import LineChart from "components/charts/LineChart";
import { getToken } from "utils/auth";

const TotalSpent = ({ loading, data, stats, onDataRefreshed }) => {
  const [localData, setLocalData] = useState(data);
  const [localStats, setLocalStats] = useState(stats);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);
  const refreshTimeoutRef = useRef(null);

  // Helper for INR formatting
  const formatINR = (value) => {
    if (typeof value !== "number") return value;
    return value.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
  };

  // Handle refresh button click
  const handleRefresh = async () => {
    if (isRefreshing) return; // Prevent multiple simultaneous requests

    setIsRefreshing(true);
    setRefreshError(null);

    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:3002";
      const token = getToken();
      const response = await fetch(`${baseUrl}/dashboard`, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error("Failed to refresh dashboard data");
      }

      const result = await response.json();
      const freshData = result.data;

      // Update local state with fresh data
      setLocalData(freshData?.monthlyRevenueChart);
      setLocalStats(freshData?.monthlyRevenueStats);

      // Call parent callback if provided
      if (onDataRefreshed) {
        onDataRefreshed(freshData);
      }

      // Show success toast
      if (window.__showToast) {
        window.__showToast("Dashboard data refreshed successfully", "success");
      }
    } catch (err) {
      setRefreshError(err.message);
      // Show error toast
      if (window.__showToast) {
        window.__showToast(
          `Failed to refresh data: ${err.message}`,
          "error"
        );
      }
    } finally {
      setIsRefreshing(false);
      // Clear error after 5 seconds
      refreshTimeoutRef.current = setTimeout(() => {
        setRefreshError(null);
      }, 5000);
    }
  };

  // Sync local state with props when they change
  useEffect(() => {
    if (!isRefreshing) {
      setLocalData(data);
      setLocalStats(stats);
    }
  }, [data, stats, isRefreshing]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  // Chart data from API or fallback
  const chartOptions = localData?.months
    ? {
      ...lineChartOptionsTotalSpent,
      xaxis: { ...lineChartOptionsTotalSpent.xaxis, categories: localData.months },
    }
    : lineChartOptionsTotalSpent;
  const chartSeries = localData?.chartData || lineChartDataTotalSpent;

  // Stats
  const mainValue = localStats?.currentMonthRevenue;
  const percent = localStats?.percentageChange;
  const trend = localStats?.trend;
  const arrowUp = trend === "increased";
  const percentColor = arrowUp ? "text-green-500" : "text-red-500";

  return (
    <>
      <style>{`
        @keyframes refresh-spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .refresh-icon-spin {
          animation: refresh-spin 1s linear infinite;
        }
      `}</style>
      <Card extra="!p-[20px] text-center">
        <div className="flex justify-between items-center mb-6">
          {/* Left: Statistics Block */}
          <div className="flex flex-col items-start">
            <p className="mt-2 text-sm text-gray-600">Total Spent</p>
            <p className="text-3xl font-bold text-navy-700 dark:text-white">
              {loading ? "-" : formatINR(mainValue)}
            </p>
            <div className="flex flex-col items-start">
              <div className="flex flex-row items-center justify-center">
                {arrowUp !== undefined && (
                  arrowUp ? (
                    <MdArrowDropUp className={`font-medium ${percentColor}`} />
                  ) : (
                    <MdArrowDropUp style={{ transform: "rotate(180deg)" }} className={`font-medium ${percentColor}`} />
                  )
                )}
                <p className={`text-sm font-bold ${percentColor}`}> {percent !== undefined ? `${arrowUp ? "+" : "-"}${Math.abs(percent)}%` : "-"} </p>
              </div>
            </div>
          </div>

          {/* Right: Refresh Button */}
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={`!linear flex items-center justify-center rounded-lg bg-lightPrimary p-2 text-brand-500 !transition !duration-200 ${isRefreshing
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-gray-100 active:bg-gray-200 dark:bg-navy-700 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/10 hover:cursor-pointer"
              }`}
            title={isRefreshing ? "Refreshing data..." : "Refresh dashboard data"}
          >
            <MdRefresh
              className={`h-5 w-5 ${isRefreshing ? "refresh-icon-spin" : ""}`}
            />
          </button>
        </div>

        {/* Chart Area */}
        <div className="h-full w-full">
          <LineChart
            options={chartOptions}
            series={chartSeries}
          />
        </div>
      </Card>
    </>
  );
};

export default TotalSpent;
